import React from 'react';
import axios from 'src/axios';
import { UserContext } from 'src/store/ContextStore';
import { CONSTANTS } from 'src/components/Constants';

const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];
const MODULE_ROUTE_MATCHERS = {
  // Pedigrees & genomic records
  pedigrees: (url) => /^\/api\/pedigree\b/.test(url),
  // Family History Questionnaire
  fhqs: (url) =>
    /^\/api\/questionnaire\b/.test(url) || /^\/questionnaire\b/.test(url),
  familyHistoryRecords: (url) => /^\/api\/proband\b/.test(url),
  customForms: (url) => /^\/api\/(forms|form-builder|workflows)\b/.test(url),
  pathways: (url) => /^\/api\/pathways\b/.test(url),
  medicareBilling: (url) => /^\/api\/medicare\b/.test(url),
  dataExplorer: (url) => /^\/api\/data-explorer\b/.test(url)
};

export const SgAccessContext = React.createContext({
  perms: null,
  allow: () => true,
  readOnly: () => false,
  canWrite: () => true
});

export default function SgAccessProvider({ children }) {
  const { user } = React.useContext(UserContext) || {};
  const isPrivileged = React.useMemo(
    () =>
      [
        CONSTANTS.userTypes.ADMIN,
        CONSTANTS.userTypes.GENETICS,
        CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN
      ].includes(user?.userType),
    [user?.userType]
  );
  const isGeneticsUser = user?.userType === CONSTANTS.userTypes.GENETICS_USER;
  const perms = user?.securityGroupPermissions || null;

  const allow = React.useCallback(
    (moduleKey, action = 'read') => {
      if (isPrivileged) return true;
      if (!isGeneticsUser) return true;

      if (!perms) return true;
      if (!perms[moduleKey]) return false;

      if (action === 'write') return !perms.readOnly;

      return true;
    },
    [isPrivileged, isGeneticsUser, perms]
  );

  const readOnly = React.useCallback(
    (moduleKey) => {
      if (isPrivileged) return false;
      if (!isGeneticsUser) return false;
      if (!perms) return false;
      return !!perms[moduleKey] && !!perms.readOnly;
    },
    [isPrivileged, isGeneticsUser, perms]
  );

  const canWrite = React.useCallback(
    (moduleKey) => allow(moduleKey, 'write'),
    [allow]
  );

  React.useEffect(() => {
    if (!isGeneticsUser || isPrivileged) return;

    const interceptorId = axios.interceptors.request.use(
      (config) => {
        const method = String(config?.method || 'get').toLowerCase();
        const rawUrl = String(config?.url || '');
        // normalize to pathname so /^\/api\/.../ works for absolute or relative URLs
        const url = (() => {
          try {
            return new URL(rawUrl, window.location.origin).pathname;
          } catch {
            return rawUrl;
          }
        })();
        if (!WRITE_METHODS.includes(method)) return config;
        for (const [moduleKey, matcher] of Object.entries(
          MODULE_ROUTE_MATCHERS
        )) {
          try {
            if (readOnly(moduleKey) && matcher(url)) {
              const err = new Error(
                `Read-only access enabled for ${moduleKey} by your security group.`
              );
              err.response = {
                status: 403,
                data: {
                  message: `Read-only access for ${moduleKey}`
                }
              };
              return Promise.reject(err);
            }
          } catch {
            // relevant error
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, [isGeneticsUser, isPrivileged, readOnly]);

  const value = React.useMemo(
    () => ({ perms, allow, readOnly, canWrite }),
    [perms, allow, readOnly, canWrite]
  );

  return (
    <SgAccessContext.Provider value={value}>
      {children}
    </SgAccessContext.Provider>
  );
}
